import { IMinUserDepricated } from "@mctypes/global.types";
import { IMeeting } from "@mctypes/meeting.types";
import { IUserPartial } from "@schemas/issueSchema";
import React from "react";
import { useHistory } from "react-router-dom";
import SplitPane from "react-split-pane";
// import "react-split-pane/lib/styles.css";
import { FailedAlert, McDropdownMultiUser, McDropdownSingleUser, McTitle, VSpace } from "../../../../../components";
import GlobalFileUploaderTable from "../../../../../components/FileUploader/GlobalFileUploaderTable";
import McInput from "../../../../../components/McInput/McInput";
import { useAppDispatch, useAppSelector } from "../../../../../hooks";
import { getProjectIdFromUrl } from "../../../../utils";
import { useUpdateMeetingAgendaMutation } from "../../meetingAgendaRTK";
import { useGetSingleMeetingInstanceQuery } from "../../meetingRTK";
import { setAgendaSplitPaneWidthInRedux } from "../../meetingSlice";
import AgendaConnectedIssues from "./AgendaConnectedIssues";
import { AgendaItemComments } from "./AgendaItemComments";
import AgendaPrepTodoItems from "./AgendaPrepTodoItems";

const AgendaItem: React.FC<{ onAgendaItemChange: IMeeting.onAgendaItemChange; agendaItem: IMeeting.AgendaItem }> = ({
  onAgendaItemChange,
  agendaItem,
}) => {
  const project = useAppSelector((state) => state.adminReducer.project);
  const history = useHistory();
  const projectId = getProjectIdFromUrl(history.location.pathname);
  const meetingId = history.location.pathname.split("/")[4];
  const dispatch = useAppDispatch();
  const splitWidth = useAppSelector((state) => state.meetingReducer.agendaSplitPaneWidth);

  const { data: singleMeeting, isError: singleMeetingError } = useGetSingleMeetingInstanceQuery({
    meetingId,
    projectId,
  });

  const [updateAgendaItem, { isError: updateAgendaItemError }] = useUpdateMeetingAgendaMutation();

  const agendaItemParticipantChange = (user: IMinUserDepricated | IUserPartial) => {
    const participants = agendaItem?.itemParticipants == null ? [] : [...agendaItem.itemParticipants];
    const participantIndex = participants.findIndex((p) => p._id === user._id);

    if (participantIndex !== -1) {
      participants.splice(participantIndex, 1);
    } else {
      participants.push(user as IMinUserDepricated);
    }
    onAgendaItemChange(agendaItem?.itemParticipants, participants, "itemParticipants");
  };

  const isError = singleMeetingError || updateAgendaItemError;
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
      }}
    >
      {/** Use ref to capture latest drag size until finished */}
      <SplitPane
        split="vertical"
        minSize={450}
        maxSize={-450}
        defaultSize={`${splitWidth}%`}
        onDragFinished={(size) => {
          const containerWidth = document.querySelector(".Resizer")?.parentElement?.clientWidth;
          if (containerWidth) {
            const percent = (size / containerWidth) * 100;
            dispatch(setAgendaSplitPaneWidthInRedux(percent));
          }
        }}
        allowResize
        style={{ position: "relative", height: "auto" }}
        pane1Style={{ padding: "1rem" }}
        pane2Style={{ padding: "1rem" }}
        resizerStyle={{
          width: "3px",
          cursor: "col-resize",
          background: "#e2e6ea",
          transition: "background .15s",
        }}
      >
        {/* Left Pane */}
        <div>
          {isError && <FailedAlert />}
          <VSpace />
          <McInput
            onBlur={(e) => onAgendaItemChange(agendaItem.title, e.target.value, "title")}
            value={agendaItem.title}
            style={{ fontSize: "1.4rem" }}
            placeholder="Skriv inn tittel på agendapunkt"
          />
          <VSpace />
          <McTitle title="Beskrivelse">
            <McInput
              onBlur={(e) => onAgendaItemChange(agendaItem.description, e.target.value, "description")}
              value={agendaItem.description}
              type="textarea"
              placeholder="Skriv inn beskrivelse"
              dynamicHeight
            />
          </McTitle>
          <VSpace />
          <McTitle title="Formål">
            <McInput
              onBlur={(e) => onAgendaItemChange(agendaItem.purpose, e.target.value, "purpose")}
              value={agendaItem.purpose}
              type="textarea"
              placeholder="Skriv inn formål"
              dynamicHeight
            />
          </McTitle>
          <VSpace />
          <McTitle title="Konklusjon">
            <McInput
              onBlur={(e) => onAgendaItemChange(agendaItem.conclusion, e.target.value, "conclusion")}
              value={agendaItem.conclusion}
              type="textarea"
              dynamicHeight
            />
          </McTitle>
          <VSpace />
          <McTitle title="Ansvarlig deltager">
            <div style={{ width: "300px" }}>
              <McDropdownSingleUser
                onClear={
                  agendaItem.assignedTo != null
                    ? () => onAgendaItemChange(agendaItem.assignedTo, null, "assignedTo")
                    : null
                }
                filterActive={project.members?.length > 10}
                options={(singleMeeting?.participants as IMeeting.ParticipantsPopulated[])?.map((p) => p.user) ?? []}
                selected={agendaItem.assignedTo}
                onChange={(user) => onAgendaItemChange(agendaItem.assignedTo, user as IMinUserDepricated, "assignedTo")}
              />
            </div>
          </McTitle>
          <VSpace />
          <McTitle title="Ansvarlig, annen">
            <div style={{ width: "300px" }}>
              <McInput
                onBlur={(e) => onAgendaItemChange(agendaItem.assignedToFreeText, e.target.value, "assignedToFreeText")}
                value={agendaItem.assignedToFreeText}
                type="text"
              />
            </div>
          </McTitle>
          <VSpace />
          <McTitle title="Deltakere">
            <div style={{ width: "300px" }}>
              <McDropdownMultiUser
                filterActive={project.members?.length > 10}
                allUsers={(singleMeeting?.participants as IMeeting.ParticipantsPopulated[])?.map((p) => p.user) ?? []}
                selectedUsers={agendaItem?.itemParticipants}
                onChange={(user) => agendaItemParticipantChange(user)}
              />
            </div>
          </McTitle>
          <VSpace />
          <AgendaPrepTodoItems agendaItem={agendaItem} />
          <VSpace />
          <McTitle title="Varighet (min)">
            <div style={{ width: "100px" }}>
              <McInput
                onBlur={(e) => onAgendaItemChange(agendaItem.duration, Number(e.target.value), "duration")}
                value={agendaItem.duration}
                type="number"
                min={1}
                max={180}
                dynamicHeight
              />
            </div>
          </McTitle>
          <VSpace />
          <McTitle title="Vedlegg">
            <GlobalFileUploaderTable path={`${projectId}/meetingagenda/${agendaItem._id}/files`} />
          </McTitle>
        </div>
        {/* Right Pane */}
        <div>
          <AgendaConnectedIssues agendaItem={agendaItem} defaultIssueBoard={singleMeeting?.defaultIssueBoard} />
          <VSpace />
          <AgendaItemComments agendaItem={agendaItem} projectId={projectId} singleMeeting={singleMeeting} />
        </div>
      </SplitPane>
    </div>
  );
};

export default AgendaItem;
